import { Dispatch } from 'react';
import { createBrowserHistory } from 'history';

import cloneDeep from 'lodash.clonedeep';
import isEqual from 'lodash.isequal';
import { IAction } from '../root/i-action';
import { IApiService } from '../../services/api-service';
import { IState } from '../root/state.interface';
import { AssetActionType } from './asset.enum';
import { assetBaseUrl } from './asset.constants';
import { assetIconCache } from '../../services/asset-icon-cache/asset-icon-cache';
import {
  IAssetTypeProperties, IAssetTypes, IFieldAsset, IGetAssetsResponse,
} from './i-asset';
import { MAX_CLIENT_DUPLICATE_CHECK } from '../../../utils/asset-type-import/constants';
import {
  IAssetTypeImportRequest,
  IAssetTypeImportResponse,
  IExistingAssetTypes,
} from '../../../utils/asset-type-import/i-asset-type-import';
import { IFormFields } from '../../../components/lib/field-edit-form/field-edit-form';

const FIXED_ASSET_TYPE_HEADERS = ['id', 'code', 'description', 'tenant'];

// The mongo projection trails a write by ~1.5s on the local stack; this leaves a margin.
export const PROJECTION_CATCHUP_MS = 2500;

// Matches the page size useAssetsData asks for when the list mounts.
const DEFAULT_ASSET_TYPES_PAGE_SIZE = 10;

const propertyNamesOf = (headers?: string[]): string[] => (headers ?? [])
  .map((header) => header.trim())
  .filter((header) => header !== '' && !FIXED_ASSET_TYPE_HEADERS.includes(header.toLowerCase()));

export interface IAssetActions {
  getAssetType(id: number): Promise<void>,
  getAssetTypes(limit?: number, skip?: number, sort?: string, filter?: string): Promise<void>,
  // eslint-disable-next-line
  addAssetType(assetTypes: any): Promise<void>,
  editAssetType(
    editableFields: Array<IFormFields>,
    assetFields: IAssetTypes,
    deletedFields: Array<number>,
    currentAssetID: string,
  ): Promise<void>,
  // eslint-disable-next-line
  updateAssetType(assetType: any, propertiesStatus: any): Promise<void>;
  deleteAssetType(propertiesId: Array<number>, id: number): Promise<void>,
  getAssetTypeProperties(): Promise<void>,
  addAssetTypeProperty(asset: IAssetTypes): Promise<void>,
  editAssetTypeProperty(assetTypeProperty: IAssetTypeProperties): Promise<void>,
  deleteAssetTypeProperty(deletedFields: Array<number>): Promise<void>,
  fetchAssetIconBlob(assetTypeCode: string): Promise<Blob | null>,
  uploadAssetIcon(assetTypeCode: string, file: Blob): Promise<void>,
  deleteAssetIcon(assetTypeCode: string): Promise<void>,
  importAssetTypes(payload: IAssetTypeImportRequest): Promise<IAssetTypeImportResponse>,
  fetchExistingAssetTypes(): Promise<IExistingAssetTypes>,
}

export class AssetsActions implements IAssetActions {
  protected readonly dispatch: Dispatch<IAction>;

  private readonly apiService: IApiService;

  private readonly state: IState;

  public constructor(
    state: IState,
    apiService: IApiService,
    dispatch: Dispatch<IAction>,
  ) {
    this.dispatch = dispatch;
    this.apiService = apiService;
    this.state = state;
  }

  public getAssetType = async (id: number): Promise<void> => {
    try {
      this.dispatch({ type: AssetActionType.GET_ASSET_TYPE_REQUEST, payload: null });
      const payload = await this.apiService.get({
        url: `/asset-manager/api/assetType/${id}`,
      });
      this.dispatch({ type: AssetActionType.GET_ASSET_TYPE_SUCCESS, payload });
    } catch (err) {
      this.dispatch({ type: AssetActionType.GET_ASSET_TYPE_FAIL, payload: err });
    }
  };

  public getAssetTypes = async (
    limit?: number,
    skip?: number,
    sort?: string,
    filter?: string,
  ): Promise<void> => {
    try {
      this.dispatch({ type: AssetActionType.GET_ASSET_TYPES_LIST_REQUEST, payload: null });

      const payload = await this.apiService.get({
        url: '/asset-manager/api/assetTypes',
        params: {
          limit, skip, filter, sort,
        },
      });
      this.dispatch({ type: AssetActionType.GET_ASSET_TYPES_LIST_SUCCESS, payload });
    } catch (err) {
      this.dispatch({ type: AssetActionType.GET_ASSET_TYPES_LIST_FAIL, payload: err });
    }
  };

  public addAssetType = async (
    // eslint-disable-next-line
    assetType: any
  ): Promise<void> => {
    try {
      this.dispatch({ type: AssetActionType.ADD_ASSET_TYPE_REQUEST, payload: null });
      const data = cloneDeep(assetType);
      delete data.properties;
      const payload: {id: number} = await this.apiService.post({
        url: `${assetBaseUrl}assetTypes`,
        data,
      });

      // eslint-disable-next-line
      assetType.properties.forEach(async (property: any) => {
        await this.apiService.post({
          url: `${assetBaseUrl}assetTypeProperties`,
          data: {
            name: property.name,
            value: property.value,
            assetType_id: payload.id,
          },
        });
      });

      createBrowserHistory().goBack();
      this.dispatch({ type: AssetActionType.ADD_ASSET_TYPE_SUCCESS, payload: assetType });
    } catch (err) {
      this.dispatch({ type: AssetActionType.ADD_ASSET_TYPE_FAIL, payload: err });
    }
  };

  // eslint-disable-next-line
  public updateAssetType = async (assetType: any, propertiesStatus: any): Promise<void> => {
    try {
      this.dispatch({ type: AssetActionType.EDIT_ASSET_TYPE_REQUEST, payload: null });

      const data = cloneDeep(assetType);
      delete data.properties;

      await this.apiService.put({
        url: `${assetBaseUrl}assetType/${assetType.id}`,
        data,
      });

      // eslint-disable-next-line
      assetType.properties.forEach(async (property: any) => {
        const status = propertiesStatus[property.id];
        if (status) {
          if (!isEqual(property, status.snapshot)) {
            // the property is modified so call update property service
            await this.apiService.put({
              url: `${assetBaseUrl}assetTypeProperty/${property.id}`,
              data: { name: property.name, value: property.value },
            });
          }
        } else {
          // no status, so it is a new property call service to add the property
          await this.apiService.post({
            url: `${assetBaseUrl}assetTypeProperties`,
            data: { assetType_id: assetType.id, name: property.name, value: property.value },
          });
        }
      });

      Object.keys(propertiesStatus).forEach(async (key) => {
        const status = propertiesStatus[key];
        if (status.removed) {
          await this.apiService.delete<null>({
            url: `${assetBaseUrl}assetTypeProperty/${status.snapshot.id}`,
          });
        }
      });

      const payload = await this.apiService.get({
        url: `/asset-manager/api/assetType/${assetType.id}`,
      });

      this.dispatch({ type: AssetActionType.EDIT_ASSET_TYPE_SUCCESS, payload });
    } catch (err) {
      this.dispatch({ type: AssetActionType.EDIT_ASSET_TYPE_FAIL, payload: err });
    }
  };

  public editAssetType = async (
    editableFields: Array<IFormFields>,
    assetFields: IAssetTypes,
    deletedFields: Array<number>,
    currentAssetID: string,
  ): Promise<void> => {
    try {
      this.dispatch({ type: AssetActionType.EDIT_ASSET_TYPE_REQUEST, payload: null });
      const asset = {
        id: assetFields.id,
        tenant: assetFields.tenant,
        code: assetFields.code,
        description: assetFields.description,
      };
      let isAssetUpdated = false;
      editableFields.forEach(async (editableField) => {
        if (editableField.id && editableField.isEdited) {
          this.editAssetTypeProperty(
            { id: editableField.id, name: editableField.name, value: editableField.value },
          );
        }
        if (!editableField.id && !editableField.isStatic) {
          this.addAssetTypeProperty({
            name: editableField.name,
            value: editableField.value,
            assetType_id: Number(currentAssetID),
          });
        }
        if (!editableField.id && editableField.isEdited && !isAssetUpdated) {
          isAssetUpdated = true;
          await this.apiService.put({
            url: `${assetBaseUrl}assetType/${currentAssetID}`,
            data: asset,
          });
        }
      });
      if (deletedFields.length) {
        this.deleteAssetTypeProperty(deletedFields);
      }
      await this.getAssetTypes();
      createBrowserHistory().goBack();
      this.dispatch({ type: AssetActionType.EDIT_ASSET_TYPE_SUCCESS, payload: asset });
    } catch (err) {
      this.dispatch({ type: AssetActionType.EDIT_ASSET_TYPE_FAIL, payload: err });
    }
  };

  public deleteAssetType = async (propertiesId: Array<number>, id: number): Promise<void> => {
    try {
      this.dispatch({ type: AssetActionType.DELETE_ASSET_TYPE_REQUEST, payload: null });

      if (propertiesId.length) {
        await Promise.all(propertiesId.map((propertyId) => (
          this.apiService.delete({
            url: `${assetBaseUrl}assetTypeProperty/${propertyId}`,
          })
        )));
      }

      await this.apiService.delete<null>({
        url: `${assetBaseUrl}assetType/${id}`,
      });

      createBrowserHistory().goBack();
      this.dispatch({ type: AssetActionType.DELETE_ASSET_TYPE_SUCCESS, payload: id });
      this.refreshAssetTypesAfterProjection();
    } catch (err) {
      this.dispatch({ type: AssetActionType.DELETE_ASSET_TYPE_FAIL, payload: err });
    }
  };

  // The list reads the mongo projection, so the page we just navigated back to still shows the
  // deleted row. Re-read once it has caught up.
  private refreshAssetTypesAfterProjection = (): void => {
    this.getAssetTypes(DEFAULT_ASSET_TYPES_PAGE_SIZE);
    setTimeout(
      () => this.getAssetTypes(DEFAULT_ASSET_TYPES_PAGE_SIZE),
      PROJECTION_CATCHUP_MS,
    );
  };

  public getAssetTypeProperties = async (): Promise<void> => {
    try {
      this.dispatch({ type: AssetActionType.GET_ASSET_TYPE_PROPERTIES_REQUEST, payload: null });
      const payload = await this.apiService.get({ url: `${assetBaseUrl}assetTypeProperties` });
      this.dispatch({ type: AssetActionType.GET_ASSET_TYPE_PROPERTIES_SUCCESS, payload });
    } catch (err) {
      this.dispatch({ type: AssetActionType.GET_ASSET_TYPE_PROPERTIES_FAIL, payload: err });
    }
  };

  public addAssetTypeProperty = async (assetTypeProperty: IAssetTypeProperties): Promise<void> => {
    try {
      this.dispatch({ type: AssetActionType.ADD_ASSET_TYPE_PROPERTY_REQUEST, payload: null });
      const payload: IFieldAsset = await this.apiService.post({
        url: `${assetBaseUrl}assetTypeProperties`,
        data: assetTypeProperty,
      });
      this.dispatch({
        type: AssetActionType.ADD_ASSET_TYPE_PROPERTY_SUCCESS,
        payload: { ...assetTypeProperty, ...payload },
      });
    } catch (err) {
      this.dispatch({ type: AssetActionType.ADD_ASSET_TYPE_PROPERTY_FAIL, payload: err });
    }
  };

  public editAssetTypeProperty = async (assetTypeProperty: IAssetTypeProperties): Promise<void> => {
    try {
      this.dispatch({ type: AssetActionType.EDIT_ASSET_TYPE_PROPERTY_REQUEST, payload: null });
      await this.apiService.put({
        url: `${assetBaseUrl}assetTypeProperty/${assetTypeProperty.id}`,
        data: assetTypeProperty,
      });
      this.dispatch({
        type: AssetActionType.EDIT_ASSET_TYPE_PROPERTY_SUCCESS, payload: assetTypeProperty,
      });
    } catch (err) {
      this.dispatch({ type: AssetActionType.EDIT_ASSET_TYPE_PROPERTY_FAIL, payload: err });
    }
  };

  public deleteAssetTypeProperty = async (deletedFields: Array<number>): Promise<void> => {
    try {
      this.dispatch({ type: AssetActionType.DELETE_ASSET_TYPE_PROPERTY_REQUEST, payload: null });
      deletedFields.forEach(async (id) => {
        await this.apiService.delete<null>({
          url: `${assetBaseUrl}assetTypeProperty/${id}`,
        });
      });
      this.dispatch({
        type: AssetActionType.DELETE_ASSET_TYPE_PROPERTY_SUCCESS, payload: deletedFields,
      });
    } catch (err) {
      this.dispatch({ type: AssetActionType.DELETE_ASSET_TYPE_PROPERTY_FAIL, payload: err });
    }
  };

  public fetchAssetIconBlob = async (assetTypeCode: string): Promise<Blob | null> => {
    try {
      const blob = await this.apiService.get<Blob>({
        url: `${assetBaseUrl}assetIcon/${encodeURIComponent(assetTypeCode)}`,
        responseType: 'blob',
        silent: true,
      });
      return blob && blob.size > 0 ? blob : null;
    } catch {
      return null;
    }
  };

  public uploadAssetIcon = async (assetTypeCode: string, file: Blob): Promise<void> => {
    await this.apiService.put({
      url: `${assetBaseUrl}assetIcon/${encodeURIComponent(assetTypeCode)}`,
      headers: { 'Content-Type': file.type || 'image/png' },
      data: file,
    });
    assetIconCache.invalidate(assetTypeCode);
  };

  public deleteAssetIcon = async (assetTypeCode: string): Promise<void> => {
    await this.apiService.delete({
      url: `${assetBaseUrl}assetIcon/${encodeURIComponent(assetTypeCode)}`,
    });
    assetIconCache.invalidate(assetTypeCode);
  };

  public importAssetTypes = async (
    payload: IAssetTypeImportRequest,
  ): Promise<IAssetTypeImportResponse> => {
    const response: IAssetTypeImportResponse = await this.apiService.post({
      url: `${assetBaseUrl}assetTypes/import`,
      data: payload,
    });
    return response;
  };

  public fetchExistingAssetTypes = async (): Promise<IExistingAssetTypes> => {
    const unavailable: IExistingAssetTypes = { codes: null, propertyNames: [] };

    try {
      const head = await this.apiService.get<IGetAssetsResponse>({
        url: `${assetBaseUrl}assetTypes`,
        params: { limit: 1, skip: 0 },
        silent: true,
      });

      const count = head?.count ?? 0;
      if (count === 0) {
        return { codes: new Set<string>(), propertyNames: propertyNamesOf(head?.headers) };
      }
      if (count > MAX_CLIENT_DUPLICATE_CHECK) {
        return { codes: null, propertyNames: propertyNamesOf(head?.headers) };
      }

      const all = await this.apiService.get<IGetAssetsResponse>({
        url: `${assetBaseUrl}assetTypes`,
        params: { limit: count, skip: 0 },
        silent: true,
      });

      const headers: string[] = all?.headers ?? [];
      const propertyNames = propertyNamesOf(headers);
      const codeIndex = headers.findIndex((header) => header.toLowerCase() === 'code');
      if (codeIndex < 0) {
        return { codes: null, propertyNames };
      }

      const records: Array<Array<string | number>> = all?.records ?? [];
      const codes = new Set(
        records
          .map((record) => String(record[codeIndex] ?? '').trim().toLowerCase())
          .filter((code) => code !== ''),
      );
      return { codes, propertyNames };
    } catch {
      return unavailable;
    }
  };
}
